import { ReactNode } from 'react';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import RadioGroup, { RadioGroupProps } from '@alltrails/shared/denali/components/RadioGroup';
import User from '@alltrails/shared/types/User';
import {
  FilterViewModel,
  FilterCheckboxViewModel,
  FilterConfig,
  FilterConfigRange,
  FilterConfigStarRating,
  FilterConfigToggleKey,
  FilterInput,
  FilterRadioViewModel,
  FilterType,
  FilterConfigSlider
} from 'types/Search';
import { Access, Difficulty } from 'types/Trails';
import SliderGroup from '../SliderGroup';
import RangeGroup from '../RangeGroup';
import StarRatingGroup from '../StarRatingGroup';
import CheckboxGroupWithIcon, { CheckboxGroupWithIconProps } from '../CheckboxGroupWithIcon';

export type Props = {
  filter: FilterViewModel;
  onChange: (changes: FilterConfig) => void;
  user?: User;
};

const filterMoreInfo: { [key: string]: { [key: string]: { title: ReactNode; content: ReactNode } } } = {
  [FilterType.DIFFICULTY]: {
    ...(['easy', 'moderate', 'hard'] as Difficulty[]).reduce(
      (memo, key) => ({
        ...memo,
        [key]: {
          title: <FormattedMessage defaultMessage="Difficulty levels" />,
          content: (
            <FormattedMessage
              values={{ p: (...chunks: ReactNode[]) => <p>{chunks}</p> }}
              defaultMessage="<p>Difficulty levels are determined by distance, elevation gain, and terrain.</p><p>For example, an easy trail would not have much elevation gain or would be a shorter distance. Easy trails can also be long flat trails such as rails-trails. A hard trail could have a significant amount of elevation gain and be long in distance or have some sort of terrain component that makes it challenging. Moderate is somewhere in between those.</p><p>The user reviews will also provide insight into trail difficulty levels as well as conditions which can change depending on season and can affect difficulty. We always recommend reading user reviews when determining a trails difficulty or using additional filters to fit your needs.</p>"
            />
          )
        }
      }),
      {}
    )
  },
  [FilterType.SUITABILITY]: {
    [Access.ADA]: {
      title: <FormattedMessage defaultMessage="Wheelchair friendly" />,
      content: (
        <FormattedMessage
          values={{ p: (...chunks: ReactNode[]) => <p>{chunks}</p> }}
          defaultMessage="<p>This rating is based on trail width, grade, cross-slopes, obstacles, surface firmness and type. Wheelchair friendly does not mean a trail is certified to be compliant with all country-specific accessibility legal standards.</p><p>Note that trail conditions can vary depending on time, weather and other events. Consider user skill and equipment type when choosing your trail.</p>"
        />
      )
    }
  }
};

const normalizeCheckboxFilterConfig = (filter: FilterCheckboxViewModel): CheckboxGroupWithIconProps['options'] =>
  Object.keys(filter.config).map((checkboxOption: FilterConfigToggleKey) => ({
    id: checkboxOption,
    selected: filter.config[checkboxOption].selected,
    testId: checkboxOption,
    text: filter.config[checkboxOption].name as string,
    moreInfo: filterMoreInfo[filter.key]?.[checkboxOption]
  }));

const normalizeRadioFilterConfig = (filter: FilterRadioViewModel): RadioGroupProps['options'] =>
  Object.keys(filter.config).map((radioOption: FilterConfigToggleKey) => ({
    selected: filter.config[radioOption].selected,
    testId: radioOption,
    text: filter.config[radioOption].name as string,
    value: radioOption
  }));

const FilterInputFactory = ({ filter, onChange, user }: Props) => {
  switch (filter.type) {
    case FilterInput.CHECKBOX:
      return (
        <CheckboxGroupWithIcon
          options={normalizeCheckboxFilterConfig(filter)}
          testId={`checkbox-group-${filter.key}`}
          onChange={(id: string, selected: boolean) => {
            const newFilterConfig: FilterConfig = {
              ...filter.config,
              [id]: {
                ...filter.config[id as FilterConfigToggleKey],
                selected
              }
            };
            onChange(newFilterConfig);
          }}
          useColumns={!!filter.extraProps?.useColumns}
        />
      );
    case FilterInput.RADIO:
      return (
        <RadioGroup
          onChange={(id: string) => {
            const newFilterConfig = Object.keys(filter.config).reduce((updatedConfig: FilterConfig, key: FilterConfigToggleKey) => {
              updatedConfig[key] = { ...filter.config[key], selected: key === id };
              return updatedConfig;
            }, {} as FilterConfig);
            onChange(newFilterConfig);
          }}
          options={normalizeRadioFilterConfig(filter)}
          testId={filter.key}
        />
      );
    case FilterInput.SLIDER:
      return <SliderGroup config={filter.config as FilterConfigSlider} handler={onChange} isProFilter={filter?.isProFilter} user={user} />;
    case FilterInput.RANGE:
      return <RangeGroup config={filter.config as FilterConfigRange} handler={onChange} />;
    case FilterInput.STAR_RATING:
      return <StarRatingGroup config={filter.config as FilterConfigStarRating} handler={onChange} />;
    default:
      // eslint-disable-next-line no-console
      console.warn(`FilterInputFactory unexpected filter type: ${JSON.stringify(filter)}`);
      return null;
  }
};

export default FilterInputFactory;
